
import React from 'react';
import { cleanURL } from '../../utils/helpers';

// --- ATOMS ---

export const Card: React.FC<{ children: React.ReactNode; className?: string; noPadding?: boolean }> = ({ 
    children, 
    className = "", 
    noPadding = false 
}) => (
    <div className={`app-card ${noPadding ? '' : 'app-card--padded'} rounded-[24px] border border-white/5 bg-[linear-gradient(180deg,rgba(18,22,30,0.92),rgba(11,14,20,0.96))] shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] ${noPadding ? '' : 'p-4 sm:p-5'} ${className}`}>
        {children}
    </div>
);

export const CardHeader: React.FC<{ title: string; icon?: React.ReactNode; iconColor?: string; children?: React.ReactNode }> = ({
    title,
    icon,
    iconColor = "text-gray-400",
    children
}) => {
    // Extract color value and build proper Tailwind classes with opacity
    const bgColorClass = iconColor.replace('text-', 'bg-') + '/10';
    const borderColorClass = iconColor.replace('text-', 'border-') + '/20';

    return (
        <div className="app-card__header mb-4 flex items-center justify-between">
            <h3 className="app-card__title flex items-center gap-3 text-lg font-bold text-white">
                {icon && (
                    <div className={`app-card__icon rounded-xl ${bgColorClass} ${iconColor} p-2`}>
                        {icon}
                    </div>
                )}
                {title}
            </h3>
            {children}
        </div>
    );
};

export const SectionLabel: React.FC<{ label: string; className?: string }> = ({ label, className = "" }) => (
    <div className={`app-section-label mb-2 text-[9px] font-black uppercase tracking-[0.18em] text-[#7E88A8] ${className}`}>{label}</div>
);

export const Tag: React.FC<{ label: string; color?: string }> = ({ label, color = "text-gray-300" }) => (
    <span className={`inline-flex cursor-default items-center rounded-full bg-white/[0.04] px-3 py-1 text-[10px] font-semibold transition-all ${color}`}>
        {label}
    </span>
);

export const DetailItem: React.FC<{ label: string; value: string | null; isLink?: boolean; allowWrap?: boolean }> = ({ label, value, isLink = false, allowWrap = false }) => (
    <div className="app-card app-card--dense group h-full rounded-[20px] bg-white/[0.03] p-4 transition-colors hover:bg-white/[0.05]">
        <SectionLabel label={label} />
        {isLink && value && value !== '#' && value !== 'Not available' ? (
            <a
                href={value.startsWith('http') ? value : `https://${value}`}
                target="_blank"
                rel="noreferrer"
                className="block truncate text-sm font-medium text-[#8DD8FF] transition-colors hover:text-white hover:underline"
            >
                {cleanURL(value)}
            </a>
        ) : (
            <div className={`text-sm font-medium text-white ${allowWrap ? 'break-words line-clamp-2' : 'truncate text-ellipsis'}`} title={value || ''}>{value || 'N/A'}</div>
        )}
    </div>
);

export const ProgressBar: React.FC<{ label: string; value: number; colorClass?: string }> = ({ 
    label, 
    value, 
    colorClass = "bg-[#5260FF]" 
}) => (
    <div className="app-card app-card--dense rounded-[20px] bg-white/[0.03] p-4">
        <SectionLabel label={label} />
        <div className="w-full bg-white/5 rounded-full h-1.5 mb-2">
            <div className={`h-1.5 rounded-full ${colorClass}`} style={{ width: `${Math.min(Math.max(value, 0), 100)}%` }}></div>
        </div>
        <div className="text-right text-xs text-white font-medium">{value}%</div>
    </div>
);

export const SimpleTabContent: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="animate-fadeIn h-full">{children}</div>
);
